import { Flex, Icon, Text } from "@chakra-ui/react";
import React, { useCallback, useRef, useState } from "react";
import { HiOutlineArrowUpTray } from "react-icons/hi2";

type DropZoneProps = {
  onFilesDropped: (files: File[]) => void;
  children: React.ReactNode;
  borderRadius?: string;
};

export const DropZone: React.FC<DropZoneProps> = ({
  onFilesDropped,
  children,
  borderRadius = "16px",
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounter.current = 0;

      const { files } = e.dataTransfer;
      if (!files.length) return;

      onFilesDropped(Array.from(files));
    },
    [onFilesDropped]
  );

  return (
    <Flex
      position="relative"
      w="100%"
      h="100%"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}

      {isDragOver && (
        <Flex
          position="absolute"
          inset="0"
          zIndex="10"
          align="center"
          justify="center"
          bg="blackAlpha.400"
          borderRadius={borderRadius}
          border="3px dashed"
          borderColor="purple.400"
          pointerEvents="none"
        >
          <Flex
            direction="column"
            align="center"
            gap="2"
            bg="white"
            px="8"
            py="6"
            borderRadius="xl"
            boxShadow="lg"
          >
            <Icon as={HiOutlineArrowUpTray} boxSize="8" color="purple.500" />
            <Text fontSize="md" fontWeight="semibold" color="gray.700">
              Drop file to analyze
            </Text>
            <Text fontSize="xs" color="gray.500">
              We will identify the document type and quality
            </Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
